import { CheckIcon } from '@heroicons/react/solid';
import { faClipboardList, faDryer } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  Outlet,
  useNavigate,
  useOutlet,
  useParams,
} from 'react-router-dom';
import { useQuery } from 'urql';
import { useTenant } from '~/App';
import { Files } from '~/components/FileLink';
import { TagsBuilder } from '~/components/TagCombobox/TagsBuilder';
import { ReadOnlyEditor } from '~/components/form/editor/ReadOnlyEditor';
import { deserializeNotes } from '~/components/job-template/JobTemplateForm';
import { getTemplateBehavior } from '~/components/job/AssetDrivenTemplateIndicator';
import { MemoGallery } from '~/components/job/JobView/Feed/Gallery';
import { TaskIcon } from '~/components/job/JobView/TaskList/TaskListItem';
import { Accordion } from '~/components/ui/Accordion';
import { ReadValue } from '~/components/ui/ReadValue';
import { StatusBadge } from '~/components/ui/StatusBadge';
import { ViewCard } from '~/components/ui/ViewCard';
import { Bubble, Section } from '~/components/ui/nucleus';
import { FragmentType, getFragmentData } from '~/gql';
import { AttributeSelectDocument } from '~/gql/graphql';
import { JobTemplateFields } from '~/graphql/fragment/JobTemplateFields';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { KebabNav } from '~/layouts/nav/KebabNav';
import { SideLayout } from '~/layouts/side/SideLayout';
import { useJobTemplateListContext } from './job-templates._index';

type Props = {
  template: FragmentType<typeof JobTemplateFields>;
  onClose: () => void;
  isPopup?: boolean;
};

export default function ViewTemplateRoute() {
  const outlet = useOutlet();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { templates } = useJobTemplateListContext();

  const template = templates?.find((template) => template.id === templateId);

  const handleClose = () => {
    navigate('..');
  };

  if (!template) {
    return null;
  }

  return (
    <div className='relative flex h-full'>
      <TemplateViewMain template={template} onClose={handleClose} />
      {outlet && (
        <div className='absolute z-50 h-full w-full xl:relative'>
          <Outlet context={{ template }} />
        </div>
      )}
    </div>
  );
}

const TemplateViewMain = ({ onClose, isPopup, ...props }: Props) => {
  const template = getFragmentData(JobTemplateFields, props.template);
  const { t } = useTranslation();
  const { id } = useTenant();
  const { isMobile } = useBreakpoint();
  const isRulesBased = getTemplateBehavior(template) === 'asset_rules';
  const [openPanelIdx, setOpenPanelIdx] = useState<number>(-1);

  const handlePanelToggle = (panelIndex: number | undefined) => {
    const nextPanelIndex = panelIndex === undefined ? -1 : panelIndex;
    setOpenPanelIdx(nextPanelIndex);
  };

  const templateType = isRulesBased ? 'asset_rules' : 'static';
  const templateTypes = [
    {
      type: 'static' as const,
      icon: faClipboardList,
      title: t('jobTemplate.type.static.title', 'Standard'),
      subtitle: t('jobTemplate.type.static.subtitle', 'all task types'),
      description: t(
        'jobTemplate.type.static.description',
        'The jobs created will match the template exactly'
      ),
    },
    {
      type: 'asset_rules' as const,
      icon: faDryer,
      title: t('jobTemplate.type.assetRules.title', 'Asset-Driven'),
      subtitle: t('jobTemplate.type.assetRules.subtitle', 'asset tasks only'),
      description: t(
        'jobTemplate.type.assetRules.description',
        'Defines rules that create tasks automatically based on selected assets. '
      ),
    },
  ];

  const selectedType = templateTypes.find((type) => type.type === templateType);

  const [result] = useQuery({
    query: AttributeSelectDocument,
    variables: { entityType: 'Site' },
    requestPolicy: 'cache-first',
  });
  const attributes = result.data?.attributes;

  const [result2] = useQuery({
    query: AttributeSelectDocument,
    variables: { entityType: 'Job' },
    requestPolicy: 'cache-first',
  });
  const allJobAttributes = result2.data?.attributes;

  const pages = [
    ...(template.isStarter && id !== '31'
      ? []
      : [
          {
            title: 'editTemplate',
            url: `/job-templates/${template.id}/edit`,
          },
        ]),
    {
      title: 'cloneTemplate',
      url: `/job-templates/${template.id}/clone`,
    },
  ];

  const notesContent = (() => {
    if (!template.notes) {
      return undefined;
    }

    try {
      return deserializeNotes(template.notes);
    } catch (error) {
      console.error('Failed to deserialize job template notes', {
        error,
        templateId: template.id,
      });
      return undefined;
    }
  })();

  const hasNotesContent =
    notesContent &&
    notesContent.blocks &&
    notesContent.blocks.some((block) => block.text?.trim());

  return (
    <SideLayout isPopup={isPopup}>
      {!isPopup && (
        <div className='block'>
          <SideLayout.Head
            onClose={onClose}
            rightSlot={<KebabNav pages={pages} />}
          >
            {template.name}
          </SideLayout.Head>
        </div>
      )}
      <SideLayout.Body>
        <StatusBadge value={template?.status} rounded className='mb-5' />

        {/* Template Type Display */}
        {selectedType && (
          <div className='mb-4'>
            <div className='relative flex items-center gap-4 rounded-xl border border-outline-variant bg-white p-4 text-left'>
              {/* Icon */}
              <div className='flex h-16 w-16 items-center justify-center rounded-full bg-shade-25 text-lg text-brand'>
                <FontAwesomeIcon icon={selectedType.icon} className='h-5 w-5' />
              </div>

              <div className='align-start flex flex-col'>
                {/* Title */}
                <h3 className='text-base font-semibold text-brand'>
                  {selectedType.title}
                </h3>

                {/* Subtitle */}
                <p className='mb-1 text-sm font-medium capitalize text-secondary'>
                  {selectedType.subtitle}
                </p>

                {/* Description */}
                <p className='text-sm text-grey-70'>
                  {selectedType.description}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Quick Create Accordion for Asset-Driven Templates */}
        {isRulesBased && (
          <Accordion
            defaultOpen={-1}
            onToggledPanel={handlePanelToggle}
            openPanel={openPanelIdx}
            className='mb-5'
          >
            <Accordion.GreyCard>
              <Accordion.Button
                summary={
                  template.quickCreate
                    ? t('jobTemplate.quickCreate.enabled', 'Enabled')
                    : t('jobTemplate.quickCreate.disabled', 'Disabled')
                }
              >
                {t('jobTemplate.quickCreate.label', 'Quick Create')}
              </Accordion.Button>
              <Accordion.Panel>
                <ReadValue
                  label={t('jobTemplate.quickCreate.label', 'Quick Create')}
                >
                  <div className='flex items-center gap-2'>
                    {template.quickCreate ? (
                      <>
                        <CheckIcon className='h-5 w-5 text-brand' />
                        <span className='text-sm text-secondary'>
                          {t('jobTemplate.quickCreate.enabled', 'Enabled')}
                        </span>
                      </>
                    ) : (
                      <span className='text-sm text-secondary'>
                        {t('jobTemplate.quickCreate.disabled', 'Disabled')}
                      </span>
                    )}
                  </div>
                </ReadValue>
              </Accordion.Panel>
            </Accordion.GreyCard>
          </Accordion>
        )}

        <div
          className={classNames('flex flex-col', isMobile ? 'gap-3' : 'gap-5')}
        >
          {(template.tags && template.tags.length > 0) ||
          hasNotesContent ||
          (template.attachments && template.attachments.length > 0) ||
          template.guestyCleaningStatus ? (
            <ViewCard label='Details'>
              <Bubble>
                <Section className={classNames(isMobile && 'px-3')}>
                  <div className='flex flex-col !items-start gap-3'>
                    {template.tags && template.tags.length > 0 && (
                      <div>
                        <ReadValue label='tag_plural'>
                          <div className='flex flex-[1_0_0] flex-wrap items-center justify-start gap-1.5'>
                            <TagsBuilder tags={template.tags ?? []} />
                          </div>
                        </ReadValue>
                      </div>
                    )}

                    {hasNotesContent && (
                      <div>
                        <ReadValue label='notes'>
                          <ReadOnlyEditor content={notesContent} />
                        </ReadValue>
                      </div>
                    )}

                    {template.attachments &&
                      template.attachments.length > 0 && (
                        <div>
                          <div className='label mb-1.5'>
                            {t('attachment', {
                              count: template.attachments.length,
                            })}
                          </div>
                          <Files attachments={template.attachments} />
                          <MemoGallery attachments={template.attachments} />
                        </div>
                      )}

                    {template.guestyCleaningStatus && (
                      <div>
                        <ReadValue label='Guesty cleaning status'>
                          <div className='flex items-center gap-2'>
                            <CheckIcon className='h-5 w-5 text-brand' />
                            Update Guesty cleaning status
                          </div>
                        </ReadValue>
                      </div>
                    )}
                  </div>
                </Section>
              </Bubble>
            </ViewCard>
          ) : null}
          {template.includeAttributes &&
            template.includeAttributes.length > 0 && (
              <ViewCard label='Place Attributes'>
                <Bubble>
                  <Section className={classNames(isMobile && 'px-3')}>
                    <div className='flex flex-col !items-start gap-1'>
                      {template.includeAttributes.map((attribute) => {
                        const found = attributes?.find(
                          ({ id }) => id === attribute
                        );
                        if (!found) {
                          return null;
                        }
                        return (
                          <p className='text-sm text-secondary' key={found.id}>
                            {(found.category ? `${found.category} \\ ` : '') +
                              found.name}
                          </p>
                        );
                      })}
                    </div>
                  </Section>
                </Bubble>
              </ViewCard>
            )}
          {template.jobAttributeIds && template.jobAttributeIds.length > 0 && (
            <ViewCard label='Custom Fields'>
              <Bubble>
                <Section className={classNames(isMobile && 'px-3')}>
                  <div className='flex flex-col !items-start gap-1'>
                    {template.jobAttributeIds.map((attribute) => {
                      const found = allJobAttributes?.find(
                        ({ id }) => id === attribute
                      );
                      if (!found) {
                        return null;
                      }
                      return (
                        <p className='text-sm text-secondary' key={found.id}>
                          {(found.category ? `${found.category} \\ ` : '') +
                            found.name}
                        </p>
                      );
                    })}
                  </div>
                </Section>
              </Bubble>
            </ViewCard>
          )}
          {template.tasks.length > 0 && (
            <ViewCard label={isRulesBased ? 'Rules' : 'Tasks'}>
              <Bubble>
                <div className='flex w-full flex-col !items-start gap-2'>
                  {template.tasks.map((task) => {
                    // Get asset types for display (for rules-based templates with Asset tasks)
                    let assetTypesDisplay: string | null = null;
                    if (
                      isRulesBased &&
                      task.type === 'Asset' &&
                      task.config?.assetTypes &&
                      task.config.assetTypes.length > 0
                    ) {
                      const maxLength = 30; // Maximum characters to display
                      const joined = task.config.assetTypes.join(', ');
                      if (joined.length <= maxLength) {
                        assetTypesDisplay = joined;
                      } else {
                        // Truncate and add ellipsis
                        assetTypesDisplay =
                          joined.substring(0, maxLength - 3) + '...';
                      }
                    }

                    return (
                      <Link
                        key={task.id}
                        to={`tasks/${task.id}`}
                        className='flex w-full items-center justify-between rounded border border-grey-10 bg-white px-3 py-2.5 text-grey-50 shadow transition-all hover:shadow-md'
                      >
                        <div className='flex min-w-0 flex-1 items-center gap-2'>
                          <span className='shrink-0'>{task.name}</span>
                          {assetTypesDisplay && (
                            <span className='max-w-32 truncate text-sm text-grey-40'>
                              ({assetTypesDisplay})
                            </span>
                          )}
                        </div>
                        <div className='ml-auto flex h-5 shrink-0 items-center justify-center text-brand'>
                          <TaskIcon
                            taskType={task.type}
                            taskDescription={task.description}
                          />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </Bubble>
            </ViewCard>
          )}
        </div>
      </SideLayout.Body>
    </SideLayout>
  );
};
